// AdminAppAcademyPage — Phase 5 list of generated Academy courses.
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen, Plus, Eye, EyeOff, Trash2, Edit, Loader2, RefreshCw,
  AlertCircle, Search, Sparkles, ExternalLink, CheckSquare, Square, Film,
} from "lucide-react";
import { adminApi } from "@/lib/adminApi";
import AdminAcademySeriesPage from "@/pages/admin/AdminAcademySeriesPage";

const LANG_BADGES = [
  { key: "en", label: "EN" },
  { key: "es", label: "ES" },
  { key: "pt", label: "PT" },
];

const AdminAppAcademyPage = () => {
  const [tab, setTab] = useState("courses");  // courses | series
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Bulk selection — set of course ids. `lastIdx` powers shift-click ranges.
  const [selected, setSelected] = useState(() => new Set());
  const [lastIdx, setLastIdx] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.listAcademyCourses(status);
      const list = Array.isArray(res) ? res : res?.items || res?.courses || [];
      setItems(list);
    } catch (e) {
      setError(e?.message || String(e));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = query.trim()
    ? items.filter((c) =>
        (c.title || "").toLowerCase().includes(query.toLowerCase()) ||
        (c.region || "").toLowerCase().includes(query.toLowerCase()) ||
        (c.slug || "").toLowerCase().includes(query.toLowerCase())
      )
    : items;

  // Upstream returns `status: "published" | "draft"`, not a `published`
  // boolean — align the check so the badge/toggle reflect reality.
  const isPublished = (c) => (c?.status || "").toLowerCase() === "published" || !!c?.published;

  const handlePublishToggle = async (c) => {
    try {
      if (isPublished(c)) await adminApi.unpublishAcademy(c.id || c._id);
      else await adminApi.publishAcademy(c.id || c._id);
      fetchAll();
    } catch (e) { setError(e?.message || String(e)); }
  };

  const handleDelete = async (c) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Supprimer le parcours "${c.title}" ?`)) return;
    try {
      await adminApi.deleteAcademy(c.id || c._id);
      setItems((prev) => prev.filter((x) => (x.id || x._id) !== (c.id || c._id)));
    } catch (e) { setError(e?.message || String(e)); }
  };

  // ── Bulk selection helpers ────────────────────────────────────────────
  const toggleRow = (id, idx, e) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (e?.shiftKey && lastIdx != null) {
        const [a, b] = [Math.min(lastIdx, idx), Math.max(lastIdx, idx)];
        for (let i = a; i <= b; i += 1) {
          const rowId = filtered[i]?.id || filtered[i]?._id;
          if (rowId) next.add(rowId);
        }
      } else if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
    setLastIdx(idx);
  };
  const toggleAll = () => {
    setSelected((prev) => {
      if (prev.size === filtered.length) return new Set();
      return new Set(filtered.map((c) => c.id || c._id));
    });
  };
  const clearSelection = () => setSelected(new Set());
  const runBulk = async (action) => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    const labels = { publish: "publier", unpublish: "mettre en brouillon", del: "supprimer" };
    // eslint-disable-next-line no-alert
    if (!window.confirm(`${labels[action][0].toUpperCase() + labels[action].slice(1)} ${ids.length} parcours ?${action === "del" ? " Cette action est irréversible." : ""}`)) return;
    setBulkBusy(true);
    try {
      const fn = {
        publish: adminApi.bulkPublishAcademy,
        unpublish: adminApi.bulkUnpublishAcademy,
        del: adminApi.bulkDeleteAcademy,
      }[action];
      const r = await fn(ids);
      // eslint-disable-next-line no-alert
      window.alert(`${r.succeeded}/${r.total} réussis${r.failed ? ` · ${r.failed} échecs` : ""}`);
      clearSelection();
      fetchAll();
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div data-testid="admin-app-academy">
      <div className="flex items-center justify-between mb-2">
        <h1 className="font-display text-3xl md:text-4xl text-[color:var(--gv-ink)]">Academy — parcours</h1>
        <div className="flex gap-2">
          <button onClick={fetchAll} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[4px] border border-[color:var(--gv-line)] bg-white text-[13px]" data-testid="academy-refresh-btn">
            <RefreshCw size={13} />
          </button>
          <Link
            to="/admin/app-academy/new"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[4px] bg-[color:var(--gv-pink)] text-white text-[13px] hover:bg-[color:var(--gv-pink-dark,#C01661)]"
            data-testid="academy-new-btn"
          >
            <Plus size={13} /> Nouveau parcours
          </Link>
        </div>
      </div>
      <p className="text-sm text-[color:var(--gv-ink)]/60 mb-4">Parcours générés par IA — création, édition, publication et traduction multilingue.</p>

      {/* Tabs — Parcours IA / Vidéos (Series) */}
      <div className="flex gap-2 border-b border-[color:var(--gv-line)] mb-6">
        <button
          onClick={() => setTab("courses")}
          className={`px-4 py-2 text-sm transition-colors border-b-2 -mb-px flex items-center gap-1.5 ${
            tab === "courses"
              ? "border-[color:var(--gv-pink)] text-[color:var(--gv-ink)] font-medium"
              : "border-transparent text-[color:var(--gv-ink)]/55 hover:text-[color:var(--gv-ink)]"
          }`}
          data-testid="academy-tab-courses"
        >
          <BookOpen size={13} /> Parcours IA
        </button>
        <button
          onClick={() => setTab("series")}
          className={`px-4 py-2 text-sm transition-colors border-b-2 -mb-px flex items-center gap-1.5 ${
            tab === "series"
              ? "border-[color:var(--gv-pink)] text-[color:var(--gv-ink)] font-medium"
              : "border-transparent text-[color:var(--gv-ink)]/55 hover:text-[color:var(--gv-ink)]"
          }`}
          data-testid="academy-tab-series"
        >
          <Film size={13} /> Vidéos (Series)
        </button>
      </div>

      {tab === "series" ? (
        <AdminAcademySeriesPage />
      ) : (
      <>
      <div className="flex gap-2 mb-4 items-center">
        <div className="relative flex-1 max-w-md">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--gv-ink)]/40" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rechercher…" className="gv-input w-full pl-8" data-testid="academy-search" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="gv-input" data-testid="academy-status-filter">
          <option value="">Tous</option>
          <option value="draft">Brouillons</option>
          <option value="published">Publiés</option>
        </select>
      </div>

      {/* Bulk selection toolbar — visible only when ≥1 row is selected. */}
      {selected.size > 0 && (
        <div
          className="flex flex-wrap items-center gap-3 mb-3 bg-[color:var(--gv-ink)] text-white rounded-[5px] px-4 py-2.5"
          data-testid="academy-bulk-toolbar"
        >
          <span className="text-sm"><strong>{selected.size}</strong> parcours sélectionné{selected.size > 1 ? "s" : ""}</span>
          <button
            onClick={() => runBulk("publish")}
            disabled={bulkBusy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-emerald-500 hover:bg-emerald-600 text-white text-xs disabled:opacity-50"
            data-testid="academy-bulk-publish"
          >
            {bulkBusy ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />}
            Publier
          </button>
          <button
            onClick={() => runBulk("unpublish")}
            disabled={bulkBusy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-slate-500 hover:bg-slate-600 text-white text-xs disabled:opacity-50"
            data-testid="academy-bulk-unpublish"
          >
            {bulkBusy ? <Loader2 size={12} className="animate-spin" /> : <EyeOff size={12} />}
            Mettre en brouillon
          </button>
          <button
            onClick={() => runBulk("del")}
            disabled={bulkBusy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-red-600 hover:bg-red-700 text-white text-xs disabled:opacity-50"
            data-testid="academy-bulk-delete"
          >
            {bulkBusy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
            Supprimer
          </button>
          <button
            onClick={clearSelection}
            className="ml-auto text-xs text-white/70 hover:text-white"
            data-testid="academy-bulk-clear"
          >
            Tout désélectionner
          </button>
        </div>
      )}

      {error && (
        <div className="bg-rose-50 border border-rose-200 rounded-[5px] p-4 text-sm text-rose-700 flex items-start gap-2 mb-4" data-testid="academy-error">
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">Impossible de récupérer les parcours</div>
            <div className="text-[12px] opacity-70 mt-1">Vérifiez que l'app.geovinum.io backend est en ligne.</div>
          </div>
        </div>
      )}

      <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] overflow-hidden">
        {loading ? (
          <div className="p-8 text-sm text-[color:var(--gv-ink)]/55 flex items-center gap-2" data-testid="academy-loading">
            <Loader2 size={14} className="animate-spin" /> Chargement…
          </div>
        ) : error ? null : filtered.length === 0 ? (
          <div className="p-8 text-sm text-[color:var(--gv-ink)]/55 flex items-center gap-2" data-testid="academy-empty">
            <BookOpen size={14} /> {query ? "Aucun résultat" : "Aucun parcours généré pour l'instant"}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[color:var(--gv-mist)] text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/60">
              <tr>
                <th className="text-left px-3 py-2.5 font-medium w-10">
                  <button
                    onClick={toggleAll}
                    className="inline-flex items-center justify-center text-[color:var(--gv-ink)]/70 hover:text-[color:var(--gv-pink)]"
                    data-testid="academy-select-all"
                    title={selected.size === filtered.length && filtered.length ? "Tout désélectionner" : "Tout sélectionner"}
                  >
                    {selected.size === filtered.length && filtered.length > 0 ? <CheckSquare size={14} /> : <Square size={14} />}
                  </button>
                </th>
                <th className="text-left px-4 py-2.5 font-medium w-14">Cover</th>
                <th className="text-left px-4 py-2.5 font-medium">Titre</th>
                <th className="text-left px-4 py-2.5 font-medium">Niveau</th>
                <th className="text-left px-4 py-2.5 font-medium">Région</th>
                <th className="text-left px-4 py-2.5 font-medium">Blocs</th>
                <th className="text-left px-4 py-2.5 font-medium">Traductions</th>
                <th className="text-left px-4 py-2.5 font-medium">Statut</th>
                <th className="text-right px-4 py-2.5 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, idx) => {
                const cid = c.id || c._id;
                const isSel = selected.has(cid);
                return (
                  <tr key={cid} className={`border-t border-[color:var(--gv-line)] hover:bg-[color:var(--gv-mist)]/30 ${isSel ? "bg-[color:var(--gv-pink)]/5" : ""}`} data-testid={`academy-row-${cid}`}>
                    <td className="px-3 py-2">
                      <button
                        onClick={(e) => toggleRow(cid, idx, e)}
                        className="inline-flex items-center justify-center text-[color:var(--gv-ink)]/60 hover:text-[color:var(--gv-pink)]"
                        data-testid={`academy-select-${cid}`}
                      >
                        {isSel ? <CheckSquare size={14} className="text-[color:var(--gv-pink)]" /> : <Square size={14} />}
                      </button>
                    </td>
                    <td className="px-4 py-2">
                      {c.cover_url ? (
                        <img src={c.cover_url} alt="" className="w-10 h-10 object-cover rounded" />
                      ) : (
                        <div className="w-10 h-10 rounded bg-[color:var(--gv-mist)] flex items-center justify-center text-[16px]">{c.emoji || "📚"}</div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-[color:var(--gv-ink)]">{c.title}</td>
                    <td className="px-4 py-2 text-[color:var(--gv-ink)]/70">{c.level || "—"}</td>
                    <td className="px-4 py-2 text-[color:var(--gv-ink)]/70">{c.region || "—"}</td>
                    <td className="px-4 py-2 text-[color:var(--gv-ink)]/60 text-[12px]">{(c.blocks || []).length}</td>
                    <td className="px-4 py-2">
                      <div className="flex gap-1">
                        {LANG_BADGES.map((l) => {
                          const st = c?.translations?.[l.key]?.status;
                          const ok = st === "done" || st === "success";
                          const pend = st === "pending" || st === "in_progress";
                          const fail = st === "failed" || st === "error";
                          return (
                            <span
                              key={l.key}
                              className={`px-1.5 py-0.5 rounded text-[10.5px] font-medium ${
                                ok ? "bg-emerald-50 text-emerald-700"
                                  : pend ? "bg-amber-50 text-amber-700"
                                  : fail ? "bg-red-50 text-red-700"
                                  : "bg-gray-50 text-gray-400"
                              }`}
                              data-testid={`academy-lang-${cid}-${l.key}`}
                            >
                              {l.label} {ok ? "✓" : pend ? "⏳" : fail ? "✗" : "—"}
                            </span>
                          );
                        })}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      {isPublished(c) ? (
                        <span className="inline-flex items-center gap-1 text-[11.5px] text-emerald-600"><Eye size={11} /> Publié</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11.5px] text-[color:var(--gv-ink)]/50"><EyeOff size={11} /> Brouillon</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="inline-flex gap-1">
                        {c.slug && (
                          <a
                            href={`https://app.geovinum.io/courses/${c.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1.5 rounded hover:bg-[color:var(--gv-mist)] text-[color:var(--gv-ink)]/50 hover:text-[color:var(--gv-pink)]"
                            title="Voir sur app.geovinum.io"
                            data-testid={`academy-view-${cid}`}
                          >
                            <ExternalLink size={13} />
                          </a>
                        )}
                        <Link to={`/admin/app-academy/edit/${cid}`} className="p-1.5 rounded hover:bg-[color:var(--gv-mist)] text-[color:var(--gv-ink)]/70" title="Éditer" data-testid={`academy-edit-${cid}`}>
                          <Edit size={13} />
                        </Link>
                        <button onClick={() => handlePublishToggle(c)} className="p-1.5 rounded hover:bg-[color:var(--gv-mist)] text-[color:var(--gv-ink)]/70" title={isPublished(c) ? "Dépublier" : "Publier"} data-testid={`academy-publish-${cid}`}>
                          {isPublished(c) ? <EyeOff size={13} /> : <Eye size={13} />}
                        </button>
                        <button onClick={() => handleDelete(c)} className="p-1.5 rounded hover:bg-red-50 text-[color:var(--gv-ink)]/40 hover:text-red-600" title="Supprimer" data-testid={`academy-delete-${cid}`}>
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <div className="text-[11.5px] text-[color:var(--gv-ink)]/50 mt-2">{filtered.length} parcours affiché{filtered.length > 1 ? "s" : ""}</div>
      </>
      )}
    </div>
  );
};

export default AdminAppAcademyPage;
